import React, { useCallback, useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import ManageTable from '../ManageTable'
import RichTextEditor from '../components/RichTextEditor'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'

const EMPTY = {
  page_id: '',
  title: '',
  subTitle: '',
  content: '',
  status: 'Active',
  menu_location: 'none',
  menu_sequence: 0,
}

function pageStatusLabel(status) {
  if (status == null || status === '') return '—'
  if (Number(status) === 1 || String(status).toLowerCase() === 'active') return 'Active'
  if (Number(status) === 0 || String(status).toLowerCase() === 'inactive') return 'Inactive'
  return String(status)
}

function PagesPage() {
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', {}, 'pages/pagesList')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load pages.')
      setRows([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const closeForm = () => {
    setShowForm(false)
    setForm(EMPTY)
  }

  const openNew = () => {
    setForm(EMPTY)
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    const st = pageStatusLabel(row.status)
    setForm({
      page_id: row.page_id || '',
      title: row.title || '',
      subTitle: row.subTitle || '',
      content: row.description || row.content || '',
      status: st === 'Inactive' ? 'Inactive' : 'Active',
      menu_location: row.menu_location || 'none',
      menu_sequence:
        row.menu_sequence != null && row.menu_sequence !== '' ? row.menu_sequence : 0,
    })
    setShowForm(true)
    setMessage(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    const payload = {
      page_id: form.page_id || '',
      title: form.title.trim(),
      subTitle: form.subTitle.trim(),
      content: form.content,
      status: form.status,
      menu_location: form.menu_location,
      menu_sequence: Number(form.menu_sequence) || 0,
    }
    const data = await PostAction('post', payload, 'pages/addPage')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Page saved.' })
      closeForm()
      await load()
    } else {
      setMessage({
        error: true,
        text: data?.message || data?.data?.message || 'Failed to save page.',
      })
    }
    setSaving(false)
  }

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    { key: 'title', label: 'Title' },
    {
      key: 'menu_location',
      label: 'Menu location',
      render: (row) => row.menu_location || 'none',
    },
    {
      key: 'menu_sequence',
      label: 'Sequence',
      render: (row) =>
        row.menu_sequence != null && row.menu_sequence !== ''
          ? row.menu_sequence
          : '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => pageStatusLabel(row.status),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            openEdit(row)
          }}
        >
          Edit
        </button>
      ),
    },
  ]

  return (
    <RequireModule moduleKey="pages">
      <PageHeader
        title="Pages"
        subtitle="CMS pages and menu placement."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add Page
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={columns}
            rows={rows}
            searchKeys={['title', 'menu_location', 'subTitle']}
          />
        )}
      </div>

      <Modal
        show={showForm}
        onHide={closeForm}
        size="xl"
        backdrop="static"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{form.page_id ? 'Edit Page' : 'Add Page'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="manage-page-form" onSubmit={onSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="p_title">
                  Title
                </label>
                <input
                  id="p_title"
                  className="form-control"
                  required
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="p_sub">
                  Sub Title
                </label>
                <input
                  id="p_sub"
                  className="form-control"
                  value={form.subTitle}
                  onChange={(e) => setForm((p) => ({ ...p, subTitle: e.target.value }))}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="p_status">
                  Status
                </label>
                <select
                  id="p_status"
                  className="form-control"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="p_menu">
                  Menu Location
                </label>
                <select
                  id="p_menu"
                  className="form-control"
                  value={form.menu_location}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, menu_location: e.target.value }))
                  }
                >
                  <option value="none">None</option>
                  <option value="header">Header</option>
                  <option value="footer">Footer</option>
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label" htmlFor="p_seq">
                  Menu Sequence
                </label>
                <input
                  id="p_seq"
                  type="number"
                  className="form-control"
                  value={form.menu_sequence}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, menu_sequence: e.target.value }))
                  }
                />
              </div>
              <div className="col-12 mb-3">
                <label className="form-label" htmlFor="p_content">
                  Content
                </label>
                <RichTextEditor
                  id="p_content"
                  value={form.content}
                  onChange={(html) => setForm((p) => ({ ...p, content: html }))}
                  placeholder="Write page content…"
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeForm}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="manage-page-form"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Page'}
          </button>
        </Modal.Footer>
      </Modal>
    </RequireModule>
  )
}

export default PagesPage
